import React from "react";
import { Typography, Divider } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./OrderTracker.css"; // Import the CSS file

import { useMediaQuery } from "@mui/material";
import { ORDER_STATUSES } from "../../constants";
import { useTranslation } from "react-i18next";
import { useOptions } from "../../context/optionContext";

const normalizeStatusKey = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

const PAST_SALES_STATUS_MAP = {
    1: "Open Order",
    2: "Delivered",
    3: "Invoiced",
    4: "Canceled",
};

const isCanceledStatus = (value) => {
    const normalized = normalizeStatusKey(value);
    return normalized === "cancel" || normalized === "canceled" || normalized === "cancelled";
};

const formatOrderDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
};

const parseOrderStatusMappings = (rawMappings = "", fallback = []) => {
    if (!rawMappings) return fallback;
    try {
        const parsed = typeof rawMappings === "string" ? JSON.parse(rawMappings) : rawMappings;
        if (!Array.isArray(parsed)) return fallback;
        const sanitized = parsed
            .map((item) => ({
                backendStatus: String(item?.backendStatus || "").trim(),
                label: String(item?.label || "").trim(),
                showInTracker: item?.showInTracker !== false && item?.showInTracker !== "false",
            }))
            .filter((item) => item.backendStatus && item.label);
        return sanitized.length ? sanitized : fallback;
    } catch {
        return fallback;
    }
};

const OrderTracker = ({ orderData }) => {
    const { order, details } = orderData;
    const {t} = useTranslation();
    const { optionKeys } = useOptions();
    const rawStatus = order.order_status;
    const parsedStatusCode = Number.parseInt(String(rawStatus || "").trim(), 10);
    const mappedStatusKey = Number.isFinite(parsedStatusCode)
        ? String(parsedStatusCode)
        : String(rawStatus || "").trim();
    const status =
        PAST_SALES_STATUS_MAP[mappedStatusKey] ||
        rawStatus;
    const defaultStatusFlow = [
        ORDER_STATUSES.PENDING,
        ORDER_STATUSES.OPEN_ORDER,
        ORDER_STATUSES.SHIPPED,
        ORDER_STATUSES.DELIVERED,
        ORDER_STATUSES.INVOICED,
        ORDER_STATUSES.CANCELED,
    ];
    const defaultStatusMappings = defaultStatusFlow.map((statusLabel) => ({
        backendStatus: statusLabel,
        label: statusLabel,
        showInTracker: true,
    }));
    const configuredStatuses = String(optionKeys?.orderStatusLabels || "")
        .split(/[\n,]+/)
        .map((value) => value.trim())
        .filter(Boolean);
    const fallbackMappings = (configuredStatuses.length > 0 ? configuredStatuses : defaultStatusFlow).map(
        (statusLabel) => ({
            backendStatus: statusLabel,
            label: statusLabel,
            showInTracker: true,
        })
    );
    const configuredMappings = parseOrderStatusMappings(
        optionKeys?.orderStatusMappings,
        fallbackMappings.length ? fallbackMappings : defaultStatusMappings
    );
    const normalizedCurrentStatus = normalizeStatusKey(status);
    const visibleStatusFlow = configuredMappings.filter((statusConfig) => {
        if (!statusConfig?.showInTracker) return false;
        const canceledStep = isCanceledStatus(statusConfig.backendStatus) || isCanceledStatus(statusConfig.label);
        if (canceledStep && !isCanceledStatus(status)) return false;
        return true;
    });
    const statusFlow = visibleStatusFlow.length
        ? visibleStatusFlow
        : [{ backendStatus: status || ORDER_STATUSES.PENDING, label: status || ORDER_STATUSES.PENDING, showInTracker: true }];
    const currentStatusLabel =
        configuredMappings.find(
            (statusConfig) =>
                normalizeStatusKey(statusConfig.backendStatus) === normalizedCurrentStatus
        )?.label || status;
    const confirmedShippingDate = details?.confirmed_shipping_date || order?.confirmed_shipping_date;
    const requestedShippingDate = details?.requested_shipping_date || order?.requested_shipping_date;
    const usesRequestedShippingDate = [
        ORDER_STATUSES.OPEN_ORDER,
        ORDER_STATUSES.SHIPPED,
        ORDER_STATUSES.DELIVERED,
        ORDER_STATUSES.CANCELED,
    ].some((statusLabel) => normalizeStatusKey(statusLabel) === normalizedCurrentStatus);
    const displayDate =
        normalizedCurrentStatus === normalizeStatusKey(ORDER_STATUSES.INVOICED)
            ? confirmedShippingDate || order?.order_created_date
            : usesRequestedShippingDate
                ? requestedShippingDate || order?.order_created_date
                : order?.order_created_date;
    const formattedDisplayDate = formatOrderDate(displayDate);
    const matchedStatusIndex = statusFlow.findIndex((step) => {
        const backendMatch = normalizeStatusKey(step.backendStatus) === normalizedCurrentStatus;
        const labelMatch = normalizeStatusKey(step.label) === normalizedCurrentStatus;
        return backendMatch || labelMatch;
    });
    const currentStatusIndex = Math.max(
        0,
        matchedStatusIndex
    );
    const statusList = statusFlow.map((statusConfig, index) => ({
        label: statusConfig.label,
        status: statusConfig.backendStatus,
        date: index === currentStatusIndex ? formattedDisplayDate : "",
    }));

    const isMobile = useMediaQuery("(max-width: 768px)");

    return (
        <>
            <div className="order-container">
                <div className="order-card">
                    <div className="order-card-content">
                        <div className="order-header">
                            <div className="order-info">
                                <Typography variant="h6" component="div">
                                    <b>{t('UI_TEXT.YOUR_ORDER_HAS_BEEN')}</b>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {/* {details.delivery_terms}{" "} */}
                                    {formattedDisplayDate}
                                </Typography>
                            </div>
                        </div>
                        <Divider className="otdivider" />

                        {!isMobile ? (
                            <div className="tracker-container">
                                <div className="order-tracking">
                                    {statusList.map((step, index) => {
                                        const isCurrentStatus = index === currentStatusIndex;
                                        const isNotComplete = index > currentStatusIndex;

                                        return (
                                            <React.Fragment key={`${step.status}-${index}`}>
                                                <div className="dot-container">
                                                    {isCurrentStatus && <CheckCircleIcon className="check-icon" />}
                                                    {!isCurrentStatus && (
                                                        <div
                                                            className={`dot ${
                                                                isNotComplete ? "notcomplete" : ""
                                                            }`}></div>
                                                    )}
                                                </div>
                                                {/* Line between steps, apply 'notcomplete' to the track line if needed */}
                                                {index < statusList.length - 1 && (
                                                    <div
                                                        className={`track-line ${
                                                            isNotComplete ? "notcomplete" : ""
                                                        }`}></div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>

                                <div className="order-status">
                                    {statusList.map((step, index) => (
                                        <div key={`${step.status}-${index}`} className="status-item">
                                            <Typography variant="body1" fontWeight={550}>{step.label}</Typography>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="tracker-container">
                                <div className="order-tracking">
                                    {statusList.map((step, index) => {
                                        const isCurrentStatus = index === currentStatusIndex;
                                        const isNotComplete = index > currentStatusIndex;

                                        return (
                                            <React.Fragment key={`${step.status}-${index}`}>
                                                <div className="dot-container">
                                                    {isCurrentStatus && <CheckCircleIcon className="check-icon" />}
                                                    {!isCurrentStatus && (
                                                        <div
                                                            className={`dot ${
                                                                isNotComplete ? "notcomplete" : ""
                                                            }`}></div>
                                                    )}
                                                </div>
                                                {/* Line between steps, apply 'notcomplete' to the track line if needed */}
                                                {index < statusList.length - 1 && (
                                                    <div
                                                        className={`track-line ${
                                                            isNotComplete ? "notcomplete" : ""
                                                        }`}></div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>

                                <div className="order-status">
                                    {statusList.map((step, index) => (
                                        <div key={`${step.status}-${index}`} className="status-item">
                                            <Typography>{step.date}</Typography>
                                            <Typography>{step.label}</Typography>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default OrderTracker;
